/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
    BehaviorSubject,
    Observable
} from 'rxjs';
import {
    delay,
    distinctUntilChanged
} from 'rxjs/operators';

/**
 * <p>Manages the loading spinner global context.
 * <p><i>This class is part of {@link SpinnerService} and is meant to be final, do <b>not</b> extend.</i>
 */
export class SpinnerGlobalContext {
    /**
     * Keeps track of how what is wanting the loading spinner.
     * @private
     */
    private set = new Set<unknown>();

    /**
     * Notifier for the loading spinner.
     * @private
     */
    private subject = new BehaviorSubject<boolean>(false);

    /**
     * Returns the observable responsible for the loading spinner.
     * @return The loading spinner observable.
     */
    onChange(): Observable<boolean> {
        return this.subject.pipe(distinctUntilChanged(), delay(0));
    }

    /**
     * Adds a loading spinner.
     * @param key The wanted loading spinner reference.
     */
    add<T>(key: T): void {
        this.set.add(key);

        this.emit();
    }

    /**
     * Removes a loading spinner.
     * @param key The wanted loading spinner reference.
     */
    remove<T>(key: T): void {
        this.set.delete(key);

        this.emit();
    }

    /**
     * Emits true if the loading spinner is being used at least once, false otherwise.
     * @private
     */
    private emit(): void {
        this.subject.next(this.set.size > 0);
    }
}
